import { useMemo } from 'react'
import { useDataValue } from 'Simple/Data.js'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  let iconOnly = useDataValue({
    context: 'task_boards_list',
    path: 'iconOnly',
    viewPath: props.viewPath,
  })

  let showPrivateIcon = useDataValue({
    context: 'task_boards_list',
    path: 'showPrivateIcon',
    viewPath: props.viewPath,
  })

  let list_type = useDataValue({
    context: 'task_boards_list',
    path: 'type',
    viewPath: props.viewPath,
  })

  let user_id = useDataValue({
    context: 'global',
    path: 'user.id',
    viewPath: props.viewPath,
  })

  return useMemo(() => {
    if (!data) return data

    // Enrich all boards with metadata
    let enriched = data.tasks_boards.map(item => ({
      ...item,
      new_tasks_count: item.stages?.[0]?.tasks_aggregate?.aggregate?.count || 0,
      iconOnly,
      showPrivateIcon,
    }))

    let isUserPrivateBoard = board =>
      board.type === 'PRIVATE' && board.created_by === user_id

    if (list_type === 'private') {
      return { boards: enriched.filter(board => isUserPrivateBoard(board)) }
    }

    // For non-private lists: exclude user's private boards entirely
    let nonPrivateBoards = enriched.filter(board => !isUserPrivateBoard(board))
    let board_order_preferences = data.user_preferences_by_pk?.board_order

    // Check if user has any preferences set
    let hasPreferences =
      board_order_preferences &&
      (board_order_preferences['my-boards'] ||
        board_order_preferences['other-boards'])

    if (!hasPreferences) {
      if (list_type === 'my-boards') {
        return {
          boards: nonPrivateBoards.filter(board =>
            board.subscribers?.some(s => s.user_id === user_id)
          ),
          board_order_preferences,
        }
      }
      if (list_type === 'other-boards') {
        return {
          boards: nonPrivateBoards.filter(
            board =>
              !board.subscribers?.some(s => s.user_id === user_id) &&
              ['PUBLIC', 'HIDDEN'].includes(board.type)
          ),
          board_order_preferences,
        }
      }
      return { boards: [], board_order_preferences }
    }

    if (list_type === 'my-boards') {
      let myBoardIds = board_order_preferences['my-boards'] || []
      return {
        boards: myBoardIds
          .map(id => nonPrivateBoards.find(b => b.id === id))
          .filter(Boolean),
        board_order_preferences,
      }
    }

    if (list_type === 'other-boards') {
      let myBoardIds = board_order_preferences['my-boards'] || []
      let otherBoardIds = board_order_preferences['other-boards'] || []
      let allPreferenceIds = new Set([...myBoardIds, ...otherBoardIds])

      // Map the other-boards IDs (in order)
      let otherBoards = otherBoardIds
        .map(id => nonPrivateBoards.find(b => b.id === id))
        .filter(Boolean)

      // Add any boards not in any preference list (fallback)
      let boardsNotInPreferences = nonPrivateBoards.filter(
        b => !allPreferenceIds.has(b.id)
      )

      return {
        boards: [...otherBoards, ...boardsNotInPreferences],
        board_order_preferences,
      }
    }

    return { boards: [], board_order_preferences }
  }, [data, iconOnly, showPrivateIcon, list_type, user_id])
}
